import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Link, navigate, useLocation } from '@reach/router';
import Text from '../components/Text';
import Button from '../components/Button';
import Logo from '../components/Logo';
import { authenticateUser } from 'reducers/auth/actions';
import { isEmailValid, isPasswordValid } from 'utils/validate';
import { durationFormatter } from 'utils/format';
import { openErrorModal } from 'reducers/modal/actions';

const LoginPage = styled.div`
  flex: 1 1 100%;
  display: flex;
  justify-content: center;
  position: relative;
  background: #e8eaeb;
`;

const LoginPanel = styled.form`
  height: 450px;
  width: 400px;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 80px;
  padding: 35px 45px 25px;
  display: flex;
  flex-direction: column;
  align-content: center;
  text-align: center;
`;

const LoginHeader = styled.div`
  display: inline-block;
  margin: 20px;
`;

const InputFields = styled.div`
  max-width: 265px;
  width: 100%;
  text-align: left;
  margin: 0px auto;
`;

const TextInput = styled.input`
  width: 100%;
  height: 30px;
  padding: 2px 8px;
  font-size: 16px;
  border-radius: 2px;
  border: ${({ border }) => border || '1px solid #E5E7EB'};
  outline: none;

  ::placeholder {
    color: #4a4a4a;
    opacity: 0.5;
  }
`;

const ForgotPassword = styled.div`
  a {
    text-decoration: none;
    color: #0779af;
    font-style: italic;
  }
`;

const DEFAULT_FORM_ERROR_MESSAGE = 'Invalid login. Please try again.';
const REACT_APP_MFA_ENABLED = window.env.REACT_APP_MFA_ENABLED === 'true';
const MAX_LOGINS_EXCEEDED_MESSAGE = lockoutTime => {
  return `This account is locked for ${durationFormatter(
    lockoutTime * 100,
    false,
    true
  )} due to excessive login attempts. Please wait or reset your password.`;
};
const UNVERIFIED_ACCOUNT_MESSAGE =
  'Account has not been verified. Please check your email.';

const Login = () => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const currentUrl = useLocation();

  const handleInputChange = setter => event => {
    setter(event.target.value);
  };

  const handleFormError = (errorStatus, errorMessage = null) => {
    if (errorStatus === 429) {
      setFormErrorMessage(MAX_LOGINS_EXCEEDED_MESSAGE(errorMessage.error));
    } else if (errorStatus === 403) {
      setFormErrorMessage(UNVERIFIED_ACCOUNT_MESSAGE);
    } else if (errorStatus === 400 || errorStatus === 500) {
      setFormErrorMessage(DEFAULT_FORM_ERROR_MESSAGE);
    } else {
      setFormErrorMessage(DEFAULT_FORM_ERROR_MESSAGE);
    }
  };

  const navigateToNextRoute = result => {
    if (result && REACT_APP_MFA_ENABLED) {
      navigate(`/mfa?email=${email}&mfaAuthToken=${result.mfaAuthToken}`, {
        state: { returnUrl: currentUrl.pathname }
      });
    } else {
      navigate(`${currentUrl.pathname}`);
    }
  };

  const isLoginValid = () => {
    const emailIsValid = isEmailValid(email);
    const passwordIsValid = isPasswordValid(password);
    if (!emailIsValid || !passwordIsValid) {
      setInvalidEmail(true);
      setInvalidPassword(true);
      return false;
    }

    setInvalidEmail(false);
    setInvalidPassword(false);
    return true;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      if (!isLoginValid()) {
        dispatch(openErrorModal('Login details invalid'));
        return;
      }

      const result = await dispatch(authenticateUser(email, password));

      if (result) {
        navigateToNextRoute(result);
      } else {
        handleFormError(DEFAULT_FORM_ERROR_MESSAGE);
      }
    } catch (err) {
      handleFormError(err.status, err.message);
    }
  };

  return (
    <LoginPage>
      <LoginPanel onSubmit={handleSubmit}>
        <Logo
          width="110px"
          height="50px"
          display="inline-block"
          margin="0px auto"
        />
        <LoginHeader>
          <Text fontSize="22px">Log In</Text>
        </LoginHeader>
        <InputFields>
          <Text fontSize="12px" margin="4px 0px">
            Email Address
          </Text>
          <TextInput
            name="email"
            type="email"
            placeholder="Email Address"
            onChange={handleInputChange(setEmail)}
            border={invalidEmail ? '1px solid #EA6E6D;' : null}
          />
          <Text fontSize="12px" margin="24px 0px 4px">
            Password
          </Text>
          <TextInput
            name="password"
            type="password"
            placeholder="Password"
            onChange={handleInputChange(setPassword)}
            border={invalidPassword ? '1px solid #EA6E6D;' : null}
          />
        </InputFields>
        <Text color="#EA6E6D" fontSize="14px" margin="12px">
          {formErrorMessage}
        </Text>
        <Button
          margin="0 auto 24px"
          color="#FFFFFF"
          padding="0px 30px"
          buttonHeight="30px"
          fontSize="14px"
          type="submit"
        >
          Log In
        </Button>
        <ForgotPassword>
          <Text fontSize="14px">
            <Link to="/forgot-password">Forgot your password?</Link>
          </Text>
        </ForgotPassword>
        <Text fontSize="14px" margin="40px 0px 2px">
          Don't have an account?
        </Text>
        <Text fontSize="14px" margin="2px 0px">
          Please contact a system administrator
        </Text>
      </LoginPanel>
    </LoginPage>
  );
};

export default Login;
